import { NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserCacheLocation, IPublicClientApplication, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular'; // Redirect component imported from msal-angular

import { BcDialogService } from '@bancolombia/design-system-web/bc-services';
import { BcButtonModule } from '@bancolombia/design-system-web/bc-button';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginHomeComponent } from './ui/login/login-home/login-home.component';
import { FacturaUseCase } from './domain/usecases/factura/factura.usecase';
import { FacturaGateway } from './domain/models/factura/gateways/factura.gateway';
import { FacturaAdapter } from './infrastructure/driven-adapter/factura/factura.adapter';
import { CoreModule } from './ui/core/core.module';
import { environments } from 'src/environments/environment';
import { FacturaEstadoGateway } from './domain/models/factura-estado/gateways/factura-estado.gateway';
import { FacturaEstadoAdapter } from './infrastructure/driven-adapter/factura-estado/factura-estado.adapter';
import { FacturaEstadoUseCase } from './domain/usecases/factura-estado/factura-estado.usecase';
import { UsuarioUseCase } from './domain/usecases/usuario/usuario.usecase';
import { UsuarioGateway } from './domain/models/usuario/gateways/usuario.gateway';
import { UsuarioAdapter } from './infrastructure/driven-adapter/usuario/usuario.adapter';
import { CausalRechazoUseCase } from './domain/usecases/causal-rechazo/causal-rechazo.usecase';
import { CausalRechazoGateway } from './domain/models/causal-rechazo/gateway/causal-rechazo.gateway';
import { CausalRechazoAdapter } from './infrastructure/driven-adapter/causal-rechazo/causal-rechazo.adapter';
import { NgxsModule } from '@ngxs/store';
import { AzureAccountState } from './infrastructure/store/azure-ad/account.state';
import { AppModalUseCase } from './domain/usecases/app-modal/app-modal.usecase';
import { VfiscalUseCase } from './domain/usecases/validacion-fiscal/vfiscal.usecase';
import { VfiscalGateway } from './domain/models/validacion-fiscal/gateways/vfiscal.gateway';
import { VfiscalAdapter } from './infrastructure/driven-adapter/validacion-fiscal/vfiscal.adapter';
import { ArchivoAdjuntoUseCase } from './domain/usecases/archivo-adjunto/archivo-adjunto.usecase';
import { ArchivoAdjuntoGateway } from './domain/models/archivo-adjunto/gateways/archivo-adjunto.gateway';
import { ArchivoAdjuntoAdapter } from './infrastructure/driven-adapter/archivo-adjunto/archivo-adjunto.adapter';


export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environments.AZURE_CONFIG.CLIENT_ID,
      redirectUri: environments.AZURE_CONFIG.REDIRECT_URI,
      authority: environments.AZURE_CONFIG.AUTHORITY,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: InteractionType.Redirect };
}

@NgModule({
  declarations: [
    AppComponent,
    LoginHomeComponent,    
  ],
  imports: [
    NgxsModule.forRoot([AzureAccountState]),
    AppRoutingModule,
    BcButtonModule,
    CoreModule,
    BrowserModule,
    MsalModule,
  ],
  providers: [
    BcDialogService,
    FacturaUseCase,
    {
      provide: FacturaGateway,
      useClass: FacturaAdapter
    },
    FacturaEstadoUseCase,
    {
      provide: FacturaEstadoGateway,
      useClass: FacturaEstadoAdapter
    },
    UsuarioUseCase,
    {
      provide: UsuarioGateway,
      useClass: UsuarioAdapter
    },
    CausalRechazoUseCase,
    {
      provide: CausalRechazoGateway,
      useClass: CausalRechazoAdapter
    },
    AppModalUseCase,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    VfiscalUseCase,
    {
      provide: VfiscalGateway,
      useClass: VfiscalAdapter
    },
    ArchivoAdjuntoUseCase,
    {
      provide: ArchivoAdjuntoGateway,
      useClass: ArchivoAdjuntoAdapter
    },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
